import React, { useEffect, useRef, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Fixed } from 'layouts';
import { useDispatch } from 'react-redux';
import {
  Alert,
  Autocomplete,
  Button,
  CircularProgress,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Add, Refresh } from '@mui/icons-material';
import { useHistory } from 'react-router-dom';
import getSimulations from 'redux/actions/Common/getSimulations';
import pauseSimulation from 'redux/actions/Common/pauseSimulation';
import retriggerSimulation from 'redux/actions/Common/retriggerSimulation';
import terminateSimulation from 'redux/actions/Common/terminateSimulation';
import getOrganizationById from 'redux/actions/Common/getOrganizationById';
import CreateSimulationDialog from './components/CreateSimulationDialog';
import SimulationHealthCard from './components/SimulationHealthCard';
import useOrgSearch from './components/useOrgSearch';
import {
  CopyableId,
  LifecycleButtons,
  StatusChips,
  formatSimDaySpeed,
  formatSimulatedElapsed,
  formatTime,
  lifecycleAlert,
  simulationDetailPath,
} from './components/simulationUtils';

const PAGE_SIZE = 20;

const LIFECYCLE_ACTIONS = {
  pause: pauseSimulation,
  retrigger: retriggerSimulation,
  terminate: terminateSimulation,
};

// Retrigger's response carries no status; the others echo the new one.
const LIFECYCLE_RESULT_STATUS = {
  pause: 'paused',
  retrigger: 'running',
  terminate: 'terminated',
};

const Simulations = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const history = useHistory();
  const isMd = useMediaQuery(theme.breakpoints.up('md'), {
    defaultMatches: true,
  });

  const [simulations, setSimulations] = useState([]);
  const [nextLastSeen, setNextLastSeen] = useState(null);
  const [fetching, setFetching] = useState(false);
  const [fetchingPagination, setFetchingPagination] = useState(false);
  const [filterOrg, setFilterOrg] = useState(null);
  // Simulation ids whose pause/retrigger call is currently in-flight.
  const [updating, setUpdating] = useState([]);
  const [alert, setAlert] = useState(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);

  // List rows only carry organizationId — names are resolved lazily and cached.
  const [orgNames, setOrgNames] = useState({});
  const requestedOrgIds = useRef(new Set());

  const orgSearch = useOrgSearch();

  const fetchFirstPage = (orgId = filterOrg?.id) => {
    setFetching(true);
    dispatch(
      getSimulations(
        orgId,
        PAGE_SIZE,
        null,
        (data) => {
          setSimulations(data?.simulations || []);
          setNextLastSeen(data?.nextLastSeen || null);
          setFetching(false);
        },
        (message) => {
          setFetching(false);
          setAlert({ severity: 'error', message });
        },
      ),
    );
  };

  const fetchNextPage = () => {
    if (!nextLastSeen) return;
    setFetchingPagination(true);
    dispatch(
      getSimulations(
        filterOrg?.id,
        PAGE_SIZE,
        nextLastSeen,
        (data) => {
          const page = data?.simulations || [];
          setSimulations((existing) => {
            const seen = new Set(existing.map((item) => item?.simulationId));
            return [
              ...existing,
              ...page.filter((it) => !seen.has(it?.simulationId)),
            ];
          });
          setNextLastSeen(data?.nextLastSeen || null);
          setFetchingPagination(false);
        },
        (message) => {
          setFetchingPagination(false);
          setAlert({ severity: 'error', message });
        },
      ),
    );
  };

  const patchSimulation = (simulationId, patch) =>
    setSimulations((existing) =>
      existing.map((item) =>
        item?.simulationId === simulationId ? { ...item, ...patch } : item,
      ),
    );

  const handleLifecycleAction = (simulationId, action) => {
    if (!simulationId || updating.includes(simulationId)) return;
    setUpdating((existing) => [...existing, simulationId]);
    dispatch(
      LIFECYCLE_ACTIONS[action](
        simulationId,
        (data) => {
          setUpdating((existing) =>
            existing.filter((id) => id !== simulationId),
          );
          patchSimulation(simulationId, {
            status: data?.status || LIFECYCLE_RESULT_STATUS[action],
            stale: false,
          });
          setAlert(lifecycleAlert(action, data));
        },
        (message) => {
          setUpdating((existing) =>
            existing.filter((id) => id !== simulationId),
          );
          setAlert({ severity: 'error', message });
        },
      ),
    );
  };

  const handleCreated = (data) => {
    setShowCreateDialog(false);
    fetchFirstPage();
    setAlert(
      data?.aiServiceStartTriggered === false
        ? {
            severity: 'warning',
            message: `Simulation ${
              data?.simulationId
            } was created, but the AI service did not start: ${data?.aiServiceError ||
              'unknown error'}. Retry via Retrigger once the underlying issue clears.`,
          }
        : {
            severity: 'success',
            message: `Simulation ${data?.simulationId} created`,
          },
    );
  };

  useEffect(() => {
    fetchFirstPage();
  }, []);

  useEffect(() => {
    const missing = Array.from(
      new Set(simulations.map((item) => item?.organizationId)),
    ).filter((id) => id && !requestedOrgIds.current.has(id));
    missing.forEach((orgId) => {
      requestedOrgIds.current.add(orgId);
      dispatch(
        getOrganizationById(
          orgId,
          { fetchUsers: false, fetchTeams: false },
          (data) => {
            if (data?.organizationName) {
              setOrgNames((existing) => ({
                ...existing,
                [orgId]: data.organizationName,
              }));
            }
          },
        ),
      );
    });
  }, [simulations]);

  const copied = () =>
    setAlert({ severity: 'success', message: 'Copied to clipboard' });

  return (
    <Fixed>
      <Box
        width={{ xs: '100vw', sm: '100vw', md: 'calc(100vw - 266px)' }}
        height={{ xs: '100vh', sm: '100vh', md: 'calc(100vh - 80px)' }}
        sx={{
          overflow: 'scroll',
          padding: '20px',
        }}
        display={'flex'}
        flexDirection={'column'}
        position={'relative'}
      >
        <Box
          display={'flex'}
          alignItems={{ xs: 'flex-start', md: 'center' }}
          justifyContent={'space-between'}
          flexDirection={{ xs: 'column', md: 'row' }}
          gap={2}
        >
          <Box>
            <Typography variant={'h6'} fontWeight={'bold'}>
              AI Simulations
            </Typography>
            <Typography variant={'body2'} color={'text.secondary'}>
              AI-driven deal roleplays — one seller agent and several buyer
              agents working a real deal autonomously.
            </Typography>
          </Box>
          <Box display={'flex'} alignItems={'center'} gap={1}>
            <Autocomplete
              size="small"
              sx={{ minWidth: isMd ? 280 : 200 }}
              options={orgSearch.organizations}
              value={filterOrg}
              loading={orgSearch.fetching}
              filterOptions={(options) => options}
              getOptionLabel={(org) => org?.organizationName || org?.id || ''}
              isOptionEqualToValue={(option, value) => option?.id === value?.id}
              onInputChange={(e, value, reason) => {
                if (reason === 'input') orgSearch.setKeyword(value);
                if (reason === 'clear') orgSearch.setKeyword('');
              }}
              onChange={(e, org) => {
                setFilterOrg(org);
                if (org?.id && org?.organizationName) {
                  setOrgNames((existing) => ({
                    ...existing,
                    [org.id]: org.organizationName,
                  }));
                }
                fetchFirstPage(org?.id);
              }}
              renderInput={(params) => (
                <TextField {...params} placeholder="Filter by organization" />
              )}
            />
            <Tooltip title="Reload">
              <span>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => fetchFirstPage()}
                  disabled={fetching}
                  startIcon={<Refresh fontSize="small" />}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  Refresh
                </Button>
              </span>
            </Tooltip>
            <Button
              size="small"
              variant="contained"
              onClick={() => setShowCreateDialog(true)}
              startIcon={<Add fontSize="small" />}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Create Simulation
            </Button>
          </Box>
        </Box>

        <SimulationHealthCard />

        {fetching ? (
          <Box
            display={'flex'}
            alignItems={'center'}
            justifyContent={'center'}
            gap={1}
            py={6}
          >
            <CircularProgress size={20} />
            <Typography variant={'body2'} color={'text.secondary'}>
              Loading simulations...
            </Typography>
          </Box>
        ) : simulations?.length > 0 ? (
          <>
            <TableContainer component={Paper} sx={{ marginTop: 2 }}>
              <Table sx={{ minWidth: 900 }} aria-label="simulations table">
                <TableHead>
                  <TableRow>
                    <TableCell>Simulation</TableCell>
                    <TableCell>Seller Org</TableCell>
                    <TableCell>Deal</TableCell>
                    <TableCell align="center">Status</TableCell>
                    <TableCell align="center">Pacing</TableCell>
                    <TableCell align="center">Tools</TableCell>
                    <TableCell align="center">Created</TableCell>
                    <TableCell align="center">Last Action</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {simulations.map((item) => {
                    const isUpdating = updating.includes(item?.simulationId);
                    return (
                      <TableRow
                        key={item?.simulationId}
                        hover
                        onClick={() =>
                          history.push(simulationDetailPath(item?.simulationId))
                        }
                        sx={{
                          cursor: 'pointer',
                          '&:last-child td, &:last-child th': { border: 0 },
                        }}
                      >
                        <TableCell component="th" scope="row">
                          <CopyableId
                            value={item?.simulationId}
                            onCopied={copied}
                          />
                        </TableCell>
                        <TableCell>
                          {orgNames[item?.organizationId] ? (
                            <Typography variant={'body2'} fontWeight={'bold'}>
                              {orgNames[item?.organizationId]}
                            </Typography>
                          ) : null}
                          <CopyableId
                            value={item?.organizationId}
                            onCopied={copied}
                          />
                        </TableCell>
                        <TableCell>
                          <CopyableId value={item?.dealId} onCopied={copied} />
                        </TableCell>
                        <TableCell align="center">
                          <Box display={'flex'} justifyContent={'center'}>
                            <StatusChips
                              status={item?.status}
                              stale={item?.stale}
                              liveStatus={item?.liveStatus}
                            />
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Typography
                            variant={'body2'}
                            sx={{ whiteSpace: 'nowrap' }}
                          >
                            {item?.tickIntervalSeconds
                              ? `${item.tickIntervalSeconds}s tick`
                              : '--'}
                          </Typography>
                          <Typography
                            variant={'caption'}
                            color={'text.secondary'}
                            sx={{ whiteSpace: 'nowrap' }}
                          >
                            {formatSimDaySpeed(item?.realMsPerSimDay)}
                          </Typography>
                          {(() => {
                            const elapsed = formatSimulatedElapsed(
                              item?.currentSimulatedTimeMs,
                              item?.simulatedStartOn,
                            );
                            return elapsed ? (
                              <Typography
                                variant={'caption'}
                                color={'text.secondary'}
                                sx={{ whiteSpace: 'nowrap', display: 'block' }}
                                title={`${elapsed.totalDays} simulated days since the deal started`}
                              >
                                Day {elapsed.totalDays} ({elapsed.breakdown})
                              </Typography>
                            ) : null;
                          })()}
                        </TableCell>
                        <TableCell align="center">
                          {item?.enabledTools?.length > 0 ? (
                            <Tooltip title={item.enabledTools.join(', ')}>
                              <Typography
                                variant={'body2'}
                                sx={{ cursor: 'default' }}
                              >
                                {item.enabledTools.length} selected
                              </Typography>
                            </Tooltip>
                          ) : (
                            <Typography
                              variant={'body2'}
                              color={'text.secondary'}
                            >
                              All
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                          {formatTime(item?.createdOn)}
                        </TableCell>
                        <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                          {formatTime(item?.lastActionOn)}
                        </TableCell>
                        <TableCell align="center">
                          <LifecycleButtons
                            status={item?.status}
                            updating={isUpdating}
                            onAction={(action) =>
                              handleLifecycleAction(item?.simulationId, action)
                            }
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            {fetchingPagination ? (
              <Box
                display={'flex'}
                justifyContent={'center'}
                width={'100%'}
                p={'10px'}
              >
                <Button
                  size="small"
                  variant="contained"
                  color="primary"
                  disabled
                >
                  Loading more simulations ...
                  <CircularProgress size={20} sx={{ marginLeft: 1 }} />
                </Button>
              </Box>
            ) : nextLastSeen ? (
              <Box
                display={'flex'}
                justifyContent={'center'}
                width={'100%'}
                p={'10px'}
              >
                <Button
                  size="small"
                  variant="contained"
                  color="primary"
                  onClick={fetchNextPage}
                  sx={{ whiteSpace: 'nowrap', minWidth: 100 }}
                >
                  Load More
                </Button>
              </Box>
            ) : null}
          </>
        ) : (
          <Box
            display={'flex'}
            flexDirection={'column'}
            alignItems={'center'}
            justifyContent={'center'}
            gap={1}
            py={6}
          >
            <Typography variant={'subtitle1'} fontWeight={'bold'}>
              No simulations
            </Typography>
            <Typography
              variant={'body2'}
              color={'text.secondary'}
              textAlign={'center'}
            >
              {filterOrg
                ? 'This organization has no simulations yet.'
                : 'No simulations have been created yet.'}
            </Typography>
            <Button
              size="small"
              variant="contained"
              onClick={() => setShowCreateDialog(true)}
              startIcon={<Add fontSize="small" />}
            >
              Create Simulation
            </Button>
          </Box>
        )}

        <CreateSimulationDialog
          open={showCreateDialog}
          onClose={() => setShowCreateDialog(false)}
          onCreated={handleCreated}
        />

        <Snackbar
          open={Boolean(alert)}
          autoHideDuration={alert?.severity === 'warning' ? 10000 : 4000}
          onClose={() => setAlert(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={() => setAlert(null)}
            severity={alert?.severity || 'info'}
            variant="filled"
            sx={{ width: '100%' }}
          >
            {alert?.message}
          </Alert>
        </Snackbar>
      </Box>
    </Fixed>
  );
};

export default Simulations;
